import { css } from '@emotion/react';
import Color from '../../../../../util/color';
import {
  CustomizableOptionInterface,
  CustomizableOptionValueInterface,
} from 'types/types';
import Label from 'components/_shared/elements/label';
import { Flex } from 'components/box';
import { useEffect, useId, useRef, useState } from 'react';

const PILL_HEIGHT = 35;
const PILL_CLASS_NAME = 'size-pill-option';

const SizePillOption = ({
  isHighlighted,
  option,
  value,
  isSelected,
  onSelect,
}: {
  isHighlighted?: boolean;
  option: CustomizableOptionInterface;
  value: CustomizableOptionValueInterface;
  isSelected: boolean;
  onSelect: (
    option: CustomizableOptionInterface,
    value: CustomizableOptionValueInterface
  ) => void;
}) => {
  const { id, label, isSoldOut } = value;

  const idRef = useId();
  const sizePillRef = useRef<HTMLDivElement>(null);

  const [diagonalDimensions, setDiagonalDimensions] = useState<{
    rotation: number;
    length: number;
  } | null>(null);

  useEffect(() => {
    if (!sizePillRef.current) return;

    const updateDimensions = () => {
      const pillWidth = sizePillRef.current?.offsetWidth ?? 0;

      if (!pillWidth) return;

      setDiagonalDimensions({
        rotation: -Math.atan2(PILL_HEIGHT, pillWidth) * (180 / Math.PI),
        length: Math.sqrt(pillWidth * pillWidth + PILL_HEIGHT * PILL_HEIGHT),
      });
    };
    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(sizePillRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const showHighlight = isHighlighted && !isSelected;

  const borderColor = showHighlight
    ? 'danger'
    : isSelected
    ? 'primary'
    : 'mediumGrey';

  const textColor = isSoldOut
    ? 'darkGrey'
    : isSelected
    ? 'primary'
    : 'darkGrey';

  return (
    <Label
      htmlFor={idRef}
      css={css`
        cursor: ${isSoldOut ? 'not-allowed' : 'pointer'};
      `}
    >
      <input
        id={idRef}
        type="radio"
        name={`${option.id}-${id}`}
        value={id}
        checked={isSelected}
        aria-disabled={isSoldOut}
        onChange={() => {
          if (isSoldOut) return;
          onSelect(option, value);
        }}
        aria-label={`${label}${isSoldOut ? ' (sold out)' : ''}`}
        css={theme => css`
          position: absolute;
          opacity: 0;

          &:focus-visible + .${PILL_CLASS_NAME} {
            border-color: ${theme.colors.primary};
            color: ${theme.colors.primary};
            font-weight: 600;
            outline: 2px solid ${theme.colors.primary};
            outline-offset: 1px;
          }
        `}
      />

      <Flex
        ref={sizePillRef}
        className={PILL_CLASS_NAME}
        alignItems="center"
        justifyContent="center"
        height={`${PILL_HEIGHT}px`}
        position="relative"
        overflow="hidden"
        borderRadius={1}
        fontWeight={isSelected ? 600 : 500}
        opacity={isSoldOut ? 0.6 : 1}
        css={theme => css`
          border: 2px solid ${theme.colors[borderColor]};
          color: ${theme.colors[textColor]};

          box-shadow: ${showHighlight &&
          `0 0 6px 2px ${Color(theme.colors.danger)
            .alpha(0.35)
            .rgb()
            .string()}`};

          ${!isSoldOut &&
          css`
            &:hover {
              border-color: ${theme.colors.primary};
              color: ${theme.colors.primary};
              font-weight: 600;
            }
          `}

          ${isSoldOut &&
          diagonalDimensions &&
          css`
            &::after {
              content: '';
              position: absolute;
              width: ${diagonalDimensions.length}px;
              height: 1px;
              background: ${theme.colors.mediumGrey};
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%)
                rotate(${diagonalDimensions.rotation}deg);
              transform-origin: center;
            }
          `}
        `}
      >
        {label}
      </Flex>
    </Label>
  );
};

export default SizePillOption;
